import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import Market from '@/creditflowresearch/domain/Market';
import UsMarket from '@/creditflowresearch/domain/Market/entities/UsMarket';
import EuropeanMarket from '@/creditflowresearch/domain/Market/entities/EuropeanMarket';
import UsoaRegion from '@/creditflowresearch/domain/Region/entities/UsoaRegion';
import EmeaRegion from '@/creditflowresearch/domain/Region/entities/EmeaRegion';
import { selectDefaultRegionId } from '@/creditflowresearch/domain/Region/selectors/selectDefaultRegionId';
import type { IRegion } from '@/creditflowresearch/domain/vo/Region';
import type { IRegionAccessControl } from '@/creditflowresearch/domain/Region/selectors/hasRegionAccess';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';

export interface IUseMarketStateResult {
  selectedMarketId: string,
  setSelectedMarketId: (marketId: string) => void,
  marketsList: any[],
  accessControl: IRegionAccessControl,
}

export const useMarketState = (preferredMarketIds?: string[]): IUseMarketStateResult => {
  const { regions: accessibleRegionsIds, plan, preferences } = useUserPlanContext();

  const accessControl = useMemo<IRegionAccessControl>(() => ({
    plan,
    accessibleRegionsIds,
  }), [plan, accessibleRegionsIds]);

  const marketsList = useMemo(() => [
    {
      id: EmeaRegion.REGION_ID,
      name: EuropeanMarket.NAME,
      abbreviation: EuropeanMarket.NAME,
      displayName: EuropeanMarket.NAME,
    },
    {
      id: UsoaRegion.REGION_ID,
      name: UsMarket.NAME,
      abbreviation: UsMarket.NAME,
      displayName: UsMarket.NAME,
    },
  ], []);

  // Map the user's preferred corporate market(s) to region ids, keeping only
  // those presented as tabs (EMEA / USOA). An EmergingMarket-only preference
  // maps to LATM and is dropped, falling back to the access-aware default.
  const preferredRegionIds = useMemo(
    () => Market.getRegionsByMarkets(preferredMarketIds ?? preferences?.marketIds ?? [])
      .map((region) => region!.getId())
      .filter((regionId) => marketsList.some((market) => market.id === regionId)),
    [preferredMarketIds, preferences?.marketIds, marketsList],
  );

  // Access-aware default: preferred (if accessible) -> first accessible -> USOA.
  const defaultMarketId = useMemo(
    () => {
      /*
      * TODO: Remove this condition when the European market opening period ends
      * */
      if (!UserPlan.isSatisfiedPremiumPlan(accessControl.plan)) {
        return EmeaRegion.REGION_ID;
      }
      return selectDefaultRegionId(
        marketsList as unknown as IRegion[],
        accessControl,
        preferredRegionIds,
      );
    },
    [marketsList, accessControl, preferredRegionIds],
  );

  // Lazy init so the correct tab shows immediately when preferences are already
  // loaded at mount (no US flash).
  const [selectedMarketId, setSelectedMarketIdState] = useState<string>(() => defaultMarketId);

  const hasUserInteractedRef = useRef(false);

  // Reconcile against an asynchronously loaded preference, but never override a
  // tab the user has explicitly chosen.
  useEffect(() => {
    if (hasUserInteractedRef.current) {
      return;
    }
    setSelectedMarketIdState((current) =>
      (current === defaultMarketId ? current : defaultMarketId));
  }, [defaultMarketId]);

  const setSelectedMarketId = useCallback((marketId: string) => {
    hasUserInteractedRef.current = true;
    setSelectedMarketIdState(marketId);
  }, []);

  return {
    selectedMarketId,
    accessControl,
    setSelectedMarketId,
    marketsList,
  };
};
